"use client";

import { HStack, Stack, Table, Box } from "@chakra-ui/react";
import { CustomPagination } from "../components/ui/custom-pagination.tsx";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import Loader from "../components/loader.tsx";
import { materialTypeMapper } from "../utils/mappers/material-type.mapper.ts";
import type { MaterialTypeEnum, PlateTypeEnum } from "@ns/types";
import { useNavigate } from "react-router-dom";
import { api } from "../api/custom-api.ts";
import { plateTypeMapper } from "../utils/mappers/plate-type.mapper.ts";
import { Button } from "../components/ui/button.tsx";
import {
  FaArrowRightFromBracket,
  FaArrowRightToBracket,
} from "react-icons/fa6";
import ControlledForm from "../components/form/controlled-form.tsx";
import { type SearchSchema, searchSchema } from "../schemas/search.schema.ts";
import { useFormContext } from "react-hook-form";
import ControlledInput from "../components/form/controlled-input.tsx";
import {
  DialogActionTrigger,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog.tsx";
import { FaTrashAlt } from "react-icons/fa";
import { Checkbox } from "../components/ui/checkbox.tsx";
import { usePagination } from "../hooks";

const WarehouseForm = () => {
  const { watch } = useFormContext<SearchSchema>();
  const search = watch("search") || "";

  const pageSize = 10;
  const navigate = useNavigate();

  const { pageIndex, setPage, nextPage, previousPage, canPreviousPage } =
    usePagination({
      pageType: "warehouse",
      search,
      pageSize,
    });

  const { data, isLoading } = useQuery(
    ["codebook", pageIndex, pageSize, search],
    () => api.codebook.getCodebook(pageSize, pageIndex * pageSize, search),
    {
      keepPreviousData: true,
    },
  );

  // Update canNextPage based on data
  const canNextPageData = data ? pageIndex < data.totalPages - 1 : false;

  const handleOnClick = useCallback(
    (id: string) => {
      navigate(`/warehouses/${id}`);
    },
    [navigate],
  );

  const queryClient = useQueryClient();

  const { mutate } = useMutation({
    mutationFn: (request: { id: string; isPublic: boolean }) => {
      return api.codebook.publishCodebook(request.id, {
        isPublic: !request.isPublic,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["codebook", pageIndex, pageSize],
      });
    },
  });

  if (isLoading) {
    return <Loader />;
  }

  return (
    <Box
      w="100%"
      h="full"
      minH="100vh"
      pt={["2rem", "3rem", "5.25rem"]}
      p={["0.5rem", "0.75rem", "1rem"]}
      bg="white"
      display="flex"
      flexDirection="column"
      alignItems="center"
    >
      {/* Header */}
      <HStack
        w="full"
        justify="space-between"
        pt={["2rem", "3rem", "5.25rem"]}
        mb="1rem"
        flexWrap="wrap"
        gap={4}
      >
        <Box fontSize={["lg", "xl", "2xl"]} fontWeight="semibold" color="black">
          LAGER
        </Box>
        <ControlledInput
          name="search"
          // @ts-ignore
          w={["100%", "20rem"]}
          placeholder="Pretraži"
        />
      </HStack>

      {/* Responsive Table Container */}
      <Stack w="full" gap="5">
        <Box overflowX="auto">
          <Table.Root
            size="sm"
            stickyHeader
            interactive
            variant="outline"
            minWidth="800px"
          >
            <Table.Header bg="gray.200">
              <Table.Row>
                {[
                  "ŠIFRA MATERIJALA",
                  "NAZIV MATERIJALA",
                  "TIP MATERIJALA",
                  "TIP PLOČE",
                  "DIMENZIJE",
                  "LOKACIJA",
                  "KOLIČINA",
                  "OBJAVLJENO",
                  "",
                  "",
                  "",
                  "",
                  "",
                ].map((title, index) => (
                  <Table.ColumnHeader
                    key={index}
                    textAlign="center"
                    className="text-black font-semibold"
                  >
                    {title}
                  </Table.ColumnHeader>
                ))}
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {data?.items?.map((item) => (
                <Table.Row
                  key={item.codebook.id}
                  className="hover:bg-gray-100 cursor-pointer"
                >
                  <Table.Cell textAlign="center" color={"black"}>
                    {item?.codebook?.codebookWarehouse?.code}
                  </Table.Cell>
                  <Table.Cell textAlign="center" color={"black"}>
                    {item?.codebook?.codebookWarehouse?.name}
                  </Table.Cell>
                  <Table.Cell textAlign="center" color={"black"}>
                    {materialTypeMapper(
                      item?.codebook?.codebookWarehouse
                        ?.type as MaterialTypeEnum,
                    )}
                  </Table.Cell>
                  <Table.Cell textAlign="center" color={"black"}>
                    {plateTypeMapper(
                      item?.codebook?.codebookWarehouse
                        ?.plateType as PlateTypeEnum,
                    )}
                  </Table.Cell>
                  <Table.Cell
                    textAlign="center"
                    color={"black"}
                  >{`${item?.codebook?.height}/${item.codebook.width}${item?.codebook.thickness ? `/${item.codebook.thickness}` : ""}`}</Table.Cell>
                  <Table.Cell textAlign="center" color={"black"}>
                    {item?.codebook?.location}
                  </Table.Cell>
                  <Table.Cell textAlign="center" color={"black"}>
                    {item?.codebook?.quantity}
                  </Table.Cell>
                  <Table.Cell textAlign="center" color={"black"}>
                    <Checkbox
                      checked={item.codebook.isPublic}
                      onChange={() =>
                        mutate({
                          id: item.codebook.id,
                          isPublic: item!.codebook!.isPublic!,
                        })
                      }
                    />
                  </Table.Cell>
                  <Table.Cell textAlign="center" color={"black"}>
                    <Button
                      size="sm"
                      variant="outline"
                      color={"#000000"}
                      backgroundColor={"#ffffff"}
                      borderColor={"#ffffff"}
                      fontWeight={"600"}
                      fontSize={"14px"}
                      borderRadius={"0.5rem"}
                      onClick={() => handleOnClick(item?.codebook.id)}
                    >
                      Pregled
                    </Button>
                  </Table.Cell>
                  <Table.Cell textAlign="center">
                    <Button
                      size="sm"
                      variant="outline"
                      color={"#000000"}
                      backgroundColor={"#ffffff"}
                      borderColor={"#ffffff"}
                      fontWeight={"600"}
                      fontSize={"14px"}
                      borderRadius={"0.5rem"}
                      onClick={() =>
                        navigate(`/warehouses/${item?.codebook?.id}/demands`)
                      }
                    >
                      Trebovanja
                    </Button>
                  </Table.Cell>
                  <Table.Cell textAlign="center">
                    <Button
                      size="sm"
                      variant="outline"
                      color={"#000000"}
                      backgroundColor={"#ffffff"}
                      borderColor={"#ffffff"}
                      fontWeight={"600"}
                      fontSize={"14px"}
                      borderRadius={"0.5rem"}
                      onClick={() =>
                        navigate(`/warehouses/${item?.codebook?.id}/entries`)
                      }
                    >
                      <FaArrowRightToBracket /> Ulazi
                    </Button>
                  </Table.Cell>
                  <Table.Cell textAlign="center">
                    <Button
                      size="sm"
                      variant="outline"
                      color={"#000000"}
                      backgroundColor={"#ffffff"}
                      borderColor={"#ffffff"}
                      fontWeight={"600"}
                      fontSize={"14px"}
                      borderRadius={"0.5rem"}
                      onClick={() =>
                        navigate(`/warehouses/${item?.codebook?.id}/exits`)
                      }
                    >
                      <FaArrowRightFromBracket /> Izlazi
                    </Button>
                  </Table.Cell>
                  <Table.Cell className={"text-black"} textAlign={"center"}>
                    <Dialog
                      id={item.codebook.id}
                      pageIndex={pageIndex}
                      pageSize={pageSize}
                    />
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
        </Box>

        {/* Pagination Controls */}
        <CustomPagination
          count={data!.totalItems}
          pageSize={pageSize}
          currentPage={pageIndex}
          onPageChange={setPage}
          onNextPage={nextPage}
          onPreviousPage={previousPage}
          canNextPage={canNextPageData}
          canPreviousPage={canPreviousPage}
          siblingCount={1}
        />
      </Stack>
    </Box>
  );
};

const Dialog = ({
  id,
  pageIndex,
  pageSize,
}: {
  id: string;
  pageIndex: number;
  pageSize: number;
}) => {
  const queryClient = useQueryClient();

  const { mutate } = useMutation({
    mutationFn: (id: string) => api.codebook.deleteCodebook(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["codebook", pageIndex, pageSize],
      });
    },
  });

  return (
    <DialogRoot role="alertdialog">
      <DialogTrigger asChild>
        <FaTrashAlt color={"black"} className={"cursor-pointer"} />
      </DialogTrigger>
      <DialogContent className={"bg-[#ffffff]"}>
        <DialogHeader>
          <DialogTitle className={"text-red-500 font-semibold text-[24px]"}>
            Upozorenje
          </DialogTitle>
        </DialogHeader>
        <DialogBody>
          <p className={"text-black"}>
            Klikom na dugme "Potvrdi" obrisaćete podatke, ukoliko to ne želite
            kliknite na dugme "Odustani".
          </p>
        </DialogBody>
        <DialogFooter>
          <DialogActionTrigger asChild>
            <Button
              size={"sm"}
              colorPalette={"red"}
              width={"5rem"}
              color={"#000000"}
              backgroundColor={"#ffffff"}
              borderColor={"#ffffff"}
              fontWeight={"600"}
              fontSize={"14px"}
              borderRadius={"0.5rem"}
              variant="outline"
            >
              Odustani
            </Button>
          </DialogActionTrigger>
          <Button
            size={"sm"}
            colorPalette={"red"}
            width={"5rem"}
            color={"#EF4444"}
            backgroundColor={"#ffffff"}
            borderColor={"#ffffff"}
            fontWeight={"600"}
            fontSize={"14px"}
            borderRadius={"0.5rem"}
            variant={"outline"}
            onClick={() => mutate(id)}
          >
            Potvrdi
          </Button>
        </DialogFooter>
        <DialogCloseTrigger />
      </DialogContent>
    </DialogRoot>
  );
};

export const WarehousePage = () => {
  return (
    <ControlledForm schema={searchSchema}>
      <WarehouseForm />
    </ControlledForm>
  );
};
